// Deeplink Landing Page — constants
// Reference: TECHNICAL DESIGN DOCUMENT — Deeplink (Universal Link) Improvement v1.0

// --- Supported destinations (Section 1.2) ---
export const DESTINATIONS = {
  FUND_TAB: "fund_tab",
  FUND_INFO: "fund_info",
  ARTICLE: "article",
  OPEN_MF: "open_mf",
  OPEN_PVD: "open_pvd",
  SOFT_ONBOARD: "soft_onboard",
  FORGOT_CREDENTIALS: "forgot_credentials",
};

// Public destinations show Screen B fallback (with web link)
// Private destinations show Screen C fallback (download only, no web fallback)
export const PUBLIC_DESTINATIONS = [
  DESTINATIONS.FUND_TAB,
  DESTINATIONS.FUND_INFO,
  DESTINATIONS.ARTICLE,
];

// Anything not in PUBLIC_DESTINATIONS is treated as private (see
// isPublicDestination) — no separate list is needed.

// --- Platforms ---
export const PLATFORMS = {
  IOS: "ios",
  ANDROID: "android",
  DESKTOP: "desktop",
};

// --- Actions returned by /resolve (Section 4.1) ---
// --- How long to wait for the app before falling back to the store (mobile) ---
// Per the design: attempt the app first, and only send someone to the store if
// that attempt has not taken them off this page by the time this elapses. If
// the app did open, the OS has backgrounded this tab and the fallback is
// skipped.
//
// The number is the whole design. iOS Safari answers a custom-scheme
// navigation with a blocking "Open this page in Principal TH?" confirm, and
// that dialog does NOT hide the page (measured on an iPhone 17 simulator,
// 2026-09-03: a page-side timer logged "STILL VISIBLE" while it was up). So
// this is not "how long the app takes to open" — it is how long someone is
// given to read a dialog and tap Open.
//
// It was 1400ms, which did not cover that for anyone, and customers who had
// the app installed were being dropped at the App Store as a result. The
// approach was never the problem; the window was.
//
// 3000ms is the customer's own figure (08/09/2026), replacing the 3500ms this
// was raised to after that incident. Keep the history above in view before
// lowering it again: anything that expires while the iOS dialog is still up
// sends someone who HAS the app to the App Store, and 1400ms is what that
// looks like in the field.
//
// Waiting costs little on the other side: the download link is on the card
// from first paint, so someone without the app can leave for the store
// immediately rather than sitting out this timer.
export const APP_OPEN_TIMEOUT_MS = 3000;

export const ACTIONS = {
  OPEN_APP: "open_app",
  OPEN_APP_LOGIN_FIRST: "open_app_login_first",
  REDIRECT_STORE: "redirect_store",
  REDIRECT_WEB: "redirect_web",
  SHOW_LANDING_PAGE: "show_landing_page",
};

// --- UI strings ---
// All three logical mobile screens share these strings per the Figma design.
// desktopRedirect* are the brief message desktop visitors see before the
// redirect to DESKTOP_REDIRECT_URL (which has no English version).
export const UI_STRINGS = {
  th: {
    headerAlliance: "In alliance with",
    langTh: "TH",
    langEn: "EN",
    title: "กรุณารอสักครู่...",
    subtitle: "กำลังเปิดแอป Principal TH!",
    downloadLink: "ดาวน์โหลด Principal TH app",
    desktopRedirectText: "กำลังไปที่หน้าแอปพลิเคชัน Principal TH…",
    desktopRedirectLink: "คลิกที่นี่หากหน้าไม่เปลี่ยนอัตโนมัติ",
  },
  en: {
    headerAlliance: "In alliance with",
    langTh: "TH",
    langEn: "EN",
    title: "Please wait a moment...",
    subtitle: "Opening Principal TH!",
    downloadLink: "Download Principal TH app",
    desktopRedirectText: "Taking you to the Principal TH app page…",
    desktopRedirectLink: "Click here if the page does not change automatically",
  },
};

// --- Firebase Analytics events (Section 3) ---
export const FIREBASE_EVENTS = {
  DEEPLINK_CLICK: "deeplink_click",
  CONTENT_VIEW: "content_view",
  APP_STORE_REDIRECT: "app_store_redirect",
  PRIVATE_JOURNEY_START: "private_journey_start",
  LOGIN_REQUIRED: "login_required",
  POST_LOGIN_CONVERSION: "post_login_conversion",
  REGISTRATION_COMPLETE: "registration_complete",
  // qr_scan removed in issue 3193 — the customer no longer wants it recorded.
};

// --- Custom URL scheme prefix ---
export const APP_SCHEME = "cpamapp://";

// --- App Store / Play Store URLs ---
// Official Principal TH listing IDs (confirmed):
//   iOS bundle id:     th.principal.principalth  → App Store id 1481176177
//   Android package:   th.principal.principalth
export const STORE_URLS = {
  IOS: "https://apps.apple.com/th/app/principal-th/id1481176177",
  ANDROID: "https://play.google.com/store/apps/details?id=th.principal.principalth",
};

// --- Desktop ---
// Issue 3193: at the customer's request a deeplink opened on a desktop browser
// no longer shows our own desktop landing page (Screens D / E, with the
// download QR); it is sent to the Principal TH app page on the public website
// instead. The same page for every environment and every destination.
export const DESKTOP_REDIRECT_URL = "https://www.principal.th/th/principalTH.html";

// How long to let `deeplink_click` leave before navigating away. gtag sends
// through sendBeacon when a page unloads, which usually survives, but a
// navigation can still cancel an in-flight request. Short enough that nobody
// experiences it as a wait.
export const DESKTOP_REDIRECT_DELAY_MS = 400;
